'use strict'
import Axios from 'axios';

// Определяем базовый URL для API
// Для Timeweb обычно используется порт 80 или прокси без порта
const baseURL = process.env.VUE_APP_API || 'http://localhost:3000/api';

const REFRESHED_TOKEN_HEADER = 'x-refreshed-token';

let axios = Axios.create({
    baseURL: baseURL,
    timeout: 30000, // 30 секунд таймаут
});

// Ленивая ссылка на Vuex store: чтобы избежать циклов модулей,
// store подставляется после инициализации в main.js через registerStore().
let _store = null;
function registerStore(store) {
    _store = store;
}
function getStore() {
    return _store;
}

// Добавляем интерцептор для автоматического добавления токена к запросам
axios.interceptors.request.use(
    config => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

// Sliding session: backend подкладывает X-Refreshed-Token, когда токен близок
// к истечению или принят под grace-периодом. Сохраняем новый токен и обновляем
// глобальный заголовок, чтобы пользователь продолжал работу без перезахода.
function applyRefreshedToken(response) {
    if (!response || !response.headers) return;
    const headers = response.headers;
    const refreshed = headers[REFRESHED_TOKEN_HEADER]
        || headers[REFRESHED_TOKEN_HEADER.toUpperCase()]
        || (typeof headers.get === 'function' ? headers.get(REFRESHED_TOKEN_HEADER) : null);
    if (!refreshed) return;
    const current = localStorage.getItem('token');
    if (refreshed === current) return;
    localStorage.setItem('token', refreshed);
    axios.defaults.headers.common['Authorization'] = `Bearer ${refreshed}`;
    const store = getStore();
    if (store) {
        store.commit('auth/SET_TOKEN', refreshed);
    }
}

axios.interceptors.response.use(
    response => {
        applyRefreshedToken(response);
        return response;
    },
    error => {
        // Даже при ошибочном ответе backend мог подложить свежий токен
        if (error && error.response) {
            applyRefreshedToken(error.response);
        }

        const status = error?.response?.status;
        const config = error?.config || {};
        const url = (config.url || '').toString();

        // 401 на не-логин эндпоинтах: токен истёк или невалиден.
        // Если был залогинен — выкидываем, показываем уведомление и просим перелогиниться.
        if (status === 401 && !config._skipAuthHandler) {
            const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register');
            const store = getStore();
            if (!isAuthEndpoint && store) {
                const wasAuthenticated = !!store.getters['auth/isAuthenticated'];
                if (wasAuthenticated) {
                    store.dispatch('auth/logout');
                    const errorCode = error.response?.data?.errorCode;
                    const message = errorCode === 'TOKEN_EXPIRED'
                        ? 'Сессия истекла, войдите снова'
                        : 'Требуется авторизация. Войдите снова, пожалуйста';
                    store.dispatch('ui/showError', message);
                    store.dispatch('ui/openAuthModal');
                }
            }
        }
        return Promise.reject(error);
    }
);

// Loyalty API endpoints
const loyaltyApi = {
    // Public endpoints (no auth required)
    getPublicSettings() {
        return axios.get('/loyalty/settings/public');
    },
    // User endpoints
    getBalance() {
        return axios.get('/loyalty/balance');
    },
    getTransactions() {
        return axios.get('/loyalty/transactions');
    },
    redeemCode(code) {
        return axios.post('/loyalty/redeem-code', { code });
    },
    getReferralCode() {
        return axios.get('/loyalty/referral-code');
    },
    useReferralCode(referralCode) {
        return axios.post('/loyalty/use-referral-code', { referralCode });
    },
    getRecyclingDropInfo() {
        return axios.get('/loyalty/recycling-drop/info');
    },
    claimRecyclingDrop(code) {
        return axios.post('/loyalty/recycling-drop/claim', { code });
    },
    
    // Public gifts catalog
    getAvailableGifts() {
        return axios.get('/loyalty/gifts');
    },

    // Admin endpoints
    admin: {
        getAllUsers() {
            return axios.get('/admin/users');
        },
        resetUserPassword(userId, newPassword) {
            return axios.post(`/admin/users/${userId}/reset-password`, { newPassword });
        },
        addPoints(phone, points, description) {
            return axios.post('/admin/loyalty/add-points', { phone, points, description });
        },
        getSettings() {
            return axios.get('/admin/loyalty/settings');
        },
        updateSettings(settings) {
            return axios.put('/admin/loyalty/settings', settings);
        },
        createCode(codeData) {
            return axios.post('/admin/loyalty/codes', codeData);
        },
        getCodes() {
            return axios.get('/admin/loyalty/codes');
        },
        deactivateCode(codeId) {
            return axios.put(`/admin/loyalty/codes/${codeId}/deactivate`);
        },
        deleteCode(codeId) {
            return axios.delete(`/admin/loyalty/codes/${codeId}`);
        },
        // Gift management (admin only)
        getGifts() {
            return axios.get('/admin/gifts');
        },
        createGift(giftData) {
            return axios.post('/admin/gifts', giftData);
        },
        updateGift(id, giftData) {
            return axios.put(`/admin/gifts/${id}`, giftData);
        },
        deleteGift(id) {
            return axios.delete(`/admin/gifts/${id}`);
        },
        getGiftCategories() {
            return axios.get('/admin/gifts/categories');
        },
        getGiftRedemptions() {
            return axios.get('/admin/gifts/redemptions');
        },
        // Events / Afisha (admin)
        getEvents() {
            return axios.get('/admin/events');
        },
        createEvent(data) {
            return axios.post('/admin/events', data);
        },
        updateEvent(id, data) {
            return axios.put(`/admin/events/${id}`, data);
        },
        deleteEvent(id) {
            return axios.delete(`/admin/events/${id}`);
        },
        getEventCategories() {
            return axios.get('/admin/events/categories');
        },
        getEventRegistrations(eventId) {
            return axios.get('/admin/events/registrations', { params: eventId ? { eventId } : {} });
        },
        adminCancelEventRegistration(id) {
            return axios.post(`/admin/events/registrations/${id}/cancel`);
        },
        // ---- История транзакций и регистраций (п.8) ----
        getTransactionsHistory(params) {
            return axios.get('/admin/history/transactions', { params });
        },
        getRegistrationsHistory(params) {
            return axios.get('/admin/history/registrations', { params });
        },
        updateUserRole(userId, role) {
            return axios.put(`/admin/users/${userId}/role`, { role });
        }
    }
};

const eventsApi = {
    getEvents() {
        return axios.get('/events');
    },
    getEvent(id) {
        return axios.get(`/events/${id}`);
    },
    register(id, data) {
        return axios.post(`/events/${id}/register`, data);
    },
    getMyRegistrations() {
        return axios.get('/events/registrations/my');
    },
    cancelRegistration(id) {
        return axios.post(`/events/registrations/${id}/cancel`);
    },
    /** Подтягивает статус платежа из ЮKassa, когда вебхук ещё не пришёл. */
    syncRegistration(id) {
        return axios.post(`/events/registrations/${id}/sync`);
    },
    imageUrl(id, index = 0, updatedAt) {
        const base = `${axios.defaults.baseURL}/events/${id}/image?index=${index}`;
        return updatedAt ? `${base}&v=${encodeURIComponent(updatedAt)}` : base;
    }
};

const recyclingApi = {
    getTabletCurrent() {
        return axios.get('/recycling-tablet/current');
    },
    getInfo() {
        return axios.get('/loyalty/recycling-drop/info');
    },
    claim(code) {
        return axios.post('/loyalty/recycling-drop/claim', { code });
    }
};

const formsApi = {
    submitRopApplication(data) {
        return axios.post('/forms/rop-application', data);
    },
    submitApplication(kind, data) {
        return axios.post(`/forms/${kind}`, data);
    },
    submitRopChecklist(data) {
        return axios.post('/forms/rop-checklist', data);
    },
    admin: {
        list(params) {
            return axios.get('/admin/forms', { params });
        },
        updateStatus(id, status) {
            return axios.put(`/admin/forms/${id}/status`, { status });
        }
    }
};

// Cashier API endpoints (for ADMIN and CASHIER roles)
const cashierApi = {
    getUsers() {
        return axios.get('/cashier/users');
    },
    searchUser(phone) {
        return axios.get('/cashier/users/search', { params: { phone } });
    },
    getPointsPresets() {
        return axios.get('/cashier/points/presets');
    },
    getPointsPresetsForUser(userId) {
        return axios.get('/cashier/points/presets/for-user', { params: { userId } });
    },
    addPoints(phone, points, description, presetId) {
        const body = { phone, points, description };
        if (presetId) {
            body.presetId = presetId;
        }
        return axios.post('/cashier/points/add', body);
    },
    getGifts() {
        return axios.get('/cashier/gifts');
    },
    getGiftsForUser(userId) {
        return axios.get('/cashier/gifts/for-user', { params: { userId } });
    },
    redeemGift(phone, giftId) {
        return axios.post('/cashier/gifts/redeem', { phone, giftId });
    },
    getRedemptionHistory() {
        return axios.get('/cashier/gifts/redemptions');
    },
    getUserRedemptions(phone) {
        return axios.get('/cashier/gifts/redemptions/user', { params: { phone } });
    }
};

export {axios, loyaltyApi, cashierApi, recyclingApi, eventsApi, formsApi, registerStore};
